import type { TSavedSlide } from "@/shared/types";
import { useGetSlideContentQuery } from "@api/slides-library";
import type { StaticEntityType } from "@config";
import { cn } from "@lib/cn";
import { SlidePreview } from "@modules/slides-library/shared";
import { memo, useEffect, useState } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { ArchivedSlideActions } from "./archived-slide-actions";

export const SingleArchivedSlide = memo(({ slide }: { slide: TSavedSlide }) => {
  const { ref, isIntersecting } = useIntersectionObserver({ threshold: 0.3 });
  const [shouldRender, setShouldRender] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: slideJSON } = useGetSlideContentQuery(
    { id: slide._id },
    { skip: !isIntersecting },
  );

  useEffect(() => {
    if (isIntersecting && slideJSON) setShouldRender(true);
    else {
      const timeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [isIntersecting, slideJSON]);

  return (
    <div
      data-menu-open={isMenuOpen}
      className={cn(
        "tw-group/slide tw-relative tw-flex tw-h-[170px] tw-w-[231px] tw-flex-col tw-gap-2 tw-rounded-xl tw-bg-white tw-p-2 tw-transition-colors",
        isMenuOpen && "tw-bg-blue-50",
        "hover:tw-bg-blue-50",
      )}
    >
      <div className="tw-relative" ref={ref}>
        <SlidePreview
          slide={slideJSON as StaticEntityType}
          isIntersecting={isIntersecting}
          shouldRender={shouldRender}
        />
      </div>
      <div className="tw-flex tw-h-6 tw-w-[218px] tw-items-center tw-gap-2">
        <span className="tw-block tw-min-w-0 tw-flex-1 tw-truncate tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-blue">
          {slide.title}
        </span>
        <ArchivedSlideActions
          slide={slide}
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
        />
      </div>
    </div>
  );
});
SingleArchivedSlide.displayName = "SingleArchivedSlide";
